/**
 * Strapi-based order search utility.
 * Replaces Algolia-based search with direct Strapi backend queries.
 */

import { BACKEND_URL } from './config';

/**
 * Helper to determine display status from fulfillment group statuses
 */
export const calculateDisplayStatus = (fulfillmentGroups) => {
    if (!fulfillmentGroups || fulfillmentGroups.length === 0) {
        return 'ongoing';
    }

    const completedStatuses = [
        'delivery_completed_to_customer',
        'pickup_completed_by_customer',
        'return_completed',
        'return_completed_for_group',
        'items_purchased'
    ];

    const canceledStatuses = ['canceled'];

    const fgStatuses = fulfillmentGroups.map(fg => fg.status);

    if (fgStatuses.every(status => completedStatuses.includes(status))) {
        return 'completed';
    }

    if (fgStatuses.every(status => canceledStatuses.includes(status))) {
        return 'canceled';
    }

    return 'ongoing';
};

/**
 * Determine which backend endpoint and orderType param to use for a given roleTab.
 * 
 * roleTab values: 'all', 'buying', 'renting', 'selling', 'lending', 'licensing'
 * 
 * - buying/renting → /orders/me (customer orders)
 * - selling/lending → /orders/merchant/me (merchant orders)
 * - all → fetch both and merge
 */
const getEndpointConfig = (roleTab) => {
    switch (roleTab) {
        case 'buying':
            return { endpoint: '/orders/me', orderType: 'buying' };
        case 'renting':
            return { endpoint: '/orders/me', orderType: 'renting' };
        case 'selling':
            return { endpoint: '/orders/merchant/me', orderType: 'selling' };
        case 'lending':
            return { endpoint: '/orders/merchant/me', orderType: 'lending' };
        case 'licensing':
            return { endpoint: '/orders/me', orderType: 'licensing' };
        case 'all':
        default:
            return { endpoint: 'both', orderType: 'all' };
    }
};

/**
 * Fetch orders from a single Strapi endpoint
 */
const fetchFromEndpoint = async (endpoint, { status, orderType, start, limit, token }) => {
    const params = new URLSearchParams({
        _start: start.toString(),
        _limit: limit.toString(),
        status: status || 'all',
        orderType: orderType || 'all'
    });

    const url = `${BACKEND_URL}${endpoint}?${params.toString()}`;

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    // Handle paginated format { data: [...], pagination: {...} }
    if (result?.data && result?.pagination) {
        return result;
    }

    // Handle array format (fallback)
    if (Array.isArray(result)) {
        return {
            data: result,
            pagination: {
                start: 0,
                limit: result.length,
                total: result.length,
                hasMore: false
            }
        };
    }

    return { data: [], pagination: { start: 0, limit, total: 0, hasMore: false } };
};

/**
 * Fetch offers and transform them into order-like objects
 */
const fetchOffers = async ({ roleTab, token, userId }) => {
    try {
        let offerQueryParams = [];
        
        if (roleTab === 'buying' || roleTab === 'renting') {
            offerQueryParams.push(`offerer=${userId}`);
        } else if (roleTab === 'selling' || roleTab === 'lending') {
            offerQueryParams.push(`merchant=${userId}`);
        } else {
            offerQueryParams.push(`offerer=${userId}`);
            offerQueryParams.push(`merchant=${userId}`);
        }

        const fetchPromises = offerQueryParams.map(param => 
            fetch(`${BACKEND_URL}/offers?${param}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }).then(r => r.ok ? r.json() : [])
        );
        
        const results = await Promise.all(fetchPromises);
        let rawOffers = results.flat();
        
        // Deduplicate by ID if 'all' role fetched both
        const uniqueOffers = Array.from(new Map(rawOffers.map(o => [o.id, o])).values());

        // Map offers to order shape
        return uniqueOffers.map(offer => ({
            id: `offer_${offer.id}`,
            isOffer: true,
            offerData: offer,
            orderNumber: `REQ-${offer.id}`,
            created_at: offer.created_at,
            status: offer.status,
            customer: offer.offerer,
            displayStatus: offer.status, // Map directly to offer status
            fulfillmentGroups: [
                {
                    id: `fg_offer_${offer.id}`,
                    merchant: offer.merchant,
                    status: offer.status,
                    orderItems: [
                        {
                            id: `item_offer_${offer.id}`,
                            product: offer.product,
                            quantity: offer.quantity,
                            price: offer.offerPrice,
                            type: offer.type,
                            rentOption: offer.rentOption,
                            isOffer: true
                        }
                    ]
                }
            ]
        }));
    } catch (e) {
        console.error("Error fetching offers", e);
        return [];
    }
};

/**
 * Main search function for orders — uses Strapi backend directly.
 */
export const searchOrders = async ({
    searchTerm = '',
    filters = {},
    sortBy = 'orders',
    offset = 0,
    limit = 5,
    userId
}, getState) => {
    try {
        if (!userId) {
            throw new Error('User ID is required for order search');
        }

        const state = getState();
        const token = state.auth?.token || localStorage.getItem('token');

        if (!token) {
            throw new Error('Authentication token not found');
        }

        const roleTab = filters.roleTab || 'all';
        const statusFilter = filters.status || 'all';
        const config = getEndpointConfig(roleTab);

        let allOrders = [];
        let totalResults = 0;

        if (config.endpoint === 'both') {
            // Fetch from both customer and merchant endpoints
            const [customerResult, merchantResult] = await Promise.all([
                fetchFromEndpoint('/orders/me', {
                    status: statusFilter,
                    orderType: 'all',
                    start: 0,
                    limit: 999, // Get all for merging
                    token
                }),
                fetchFromEndpoint('/orders/merchant/me', {
                    status: statusFilter,
                    orderType: 'all',
                    start: 0,
                    limit: 999,
                    token
                }).catch(() => ({ data: [], pagination: { total: 0 } })) // Graceful fallback if not a merchant
            ]);

            // Merge and deduplicate by order ID
            const orderMap = new Map();

            customerResult.data.forEach(order => {
                orderMap.set(order.id, { ...order, _source: 'customer' });
            });

            merchantResult.data.forEach(order => {
                if (!orderMap.has(order.id)) {
                    orderMap.set(order.id, { ...order, _source: 'merchant' });
                }
            });

            // Sort by created_at descending
            allOrders = Array.from(orderMap.values());
        } else {
            // Single endpoint fetch, but fetch ALL so we can sort with offers
            const result = await fetchFromEndpoint(config.endpoint, {
                status: statusFilter,
                orderType: config.orderType,
                start: 0, // get all to merge with offers
                limit: 999,
                token
            });

            allOrders = result.data;
        }

        // --- Fetch and Merge Offers ---
        const mappedOffers = await fetchOffers({ roleTab, token, userId });
        
        // Filter offers based on status filter
        const filteredOffers = mappedOffers.filter(offer => {
            if (statusFilter === 'all') return true;
            const statusMap = {
                ongoing: ['pending', 'countered', 'accepted'],
                completed: ['completed'],
                canceled: ['rejected', 'expired']
            };
            return statusMap[statusFilter]?.includes(offer.status);
        });

        // Combine orders and offers
        allOrders = [...allOrders, ...filteredOffers];
        
        // Sort everything by created_at descending
        allOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // Client-side text search filtering (if searchTerm provided)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            allOrders = allOrders.filter(order => {
                // Search in order number
                if (order.orderNumber?.toLowerCase().includes(term)) return true;
                // Search in product names, brands, categories
                const items = order.fulfillmentGroups?.flatMap(fg => fg.orderItems || []) || [];
                return items.some(item => {
                    const product = item.product || {};
                    return (
                        product.name?.toLowerCase().includes(term) ||
                        product.brand?.toLowerCase().includes(term) ||
                        product.category?.toLowerCase().includes(term) ||
                        product.description?.toLowerCase().includes(term)
                    );
                });
            });
        }
        
        totalResults = allOrders.length;

        // Apply pagination entirely on client side now since we merge
        const paginatedOrders = allOrders.slice(offset, offset + limit);

        // Process orders
        const processedOrders = paginatedOrders.map(order => {
            if (order.isOffer) {
                return { ...order, source: 'strapi_offer' };
            }
            return {
                ...order,
                displayStatus: calculateDisplayStatus(order.fulfillmentGroups),
                source: 'strapi'
            };
        });

        const currentPage = Math.floor(offset / limit);
        const totalPages = Math.ceil(totalResults / limit);

        return {
            orders: processedOrders,
            totalResults,
            currentPage,
            totalPages,
            hasMore: offset + paginatedOrders.length < totalResults
        };
    } catch (error) {
        console.error('Order search error:', error);
        throw error;
    }
};
